import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { LEAD_STATES_URL } from "../../constants/api";

// The value the API uses for leads that have no state.
export const BLANK_STATE = "__blank__";

/**
 * The State filter for the sales reports. Lists every state the user's leads
 * carry, with a count, and "No state" for the leads without one.
 *
 * Controlled: pass `value` and `onChange(newValue)`. '' means all states. The
 * page sends `value` to its report API as the `state` param.
 *
 * `options` ([{state, count}]) is for a report whose API returns its own
 * `state_counts`: the counts then follow that report's date and rep filters.
 * Without it the dropdown lists every state across the user's leads.
 */
export default function StateFilterSelect({ value, onChange, options: reportOptions, className = "" }) {
    const { fetch } = useApiMethods();
    const [allOptions, setAllOptions] = useState([]);
    const useReport = Array.isArray(reportOptions);

    useEffect(() => {
        if (useReport) return;
        fetch(LEAD_STATES_URL).then(res => {
            if (res?.status === "success" && Array.isArray(res.data)) setAllOptions(res.data);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [useReport]);

    const options = useReport ? reportOptions : allOptions;

    const named = options.filter(o => o.state !== BLANK_STATE);
    const blank = options.find(o => o.state === BLANK_STATE);
    // A remembered pick that no longer matches any lead must still show as selected.
    const missing = value && !options.some(o => o.state.toLowerCase() === value.toLowerCase());

    return (
        <div className={`relative ${className}`}>
            <select value={value || ""} onChange={e => onChange(e.target.value)}
                title="Show only leads from one state"
                className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
            >
                <option value="">All States</option>
                {named.map(o => <option key={o.state} value={o.state}>{o.state} ({o.count})</option>)}
                {blank && <option value={BLANK_STATE}>No state ({blank.count})</option>}
                {missing && <option value={value}>{value === BLANK_STATE ? "No state" : value}</option>}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
        </div>
    );
}
